import React, { useContext, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./teamPage.css";
import { CombinedContext } from "../../context/combinedContext";
import companyBasedRequest from "../../request/companyBasedRequest";
import { Camera, Clock, Edit, House, Mail, Monitor, Phone, Rocket, UserPlus, Users } from "lucide-react";
import translations from "../../translations/translations";
import TeamServicesModal from "./staffServicesModal/teamServicesModal";
import ProfileUploadModal from "./profilePhotoModal/profilePhotoModal";
import StaffInfoModal from "./staffInfoModal/staffInfoModal";
import TeamPageSkeleton from "./teamPageSkeleton/teamPageSkeleton";
import TeamModal from "./teamModal/teamModal";
import OwnerCalendarCard from "./ownerCalendarCard/ownerCalendarCard";
import { ownerFirst } from "../../utils/ownerFirst";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";

/* Height of the sticky category bar — keep in sync with --sp-sticky in teamPage.css */
const STICKY_OFFSET = 78;

const CURRENCY_SYMBOLS = { USD: "$", DOP: "RD$", EUR: "€", CAD: "C$", MXN: "MX$" };

const formatPrice = (price, currency) => {
	const code = currency || "USD";
	const amount = Number(price ?? 0).toLocaleString("en-US");
	return CURRENCY_SYMBOLS[code] ? `${CURRENCY_SYMBOLS[code]}${amount}` : `${code} ${amount}`;
};

const formatPhone = (number) => {
	if (!number) return "";
	const digits = number.replace(/\D/g, "");
	if (digits.length === 11 && digits.startsWith("1")) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return number.startsWith("+") ? number : `+${number}`;
};

/* Status drives colour, so it has to survive an unknown value from the API. */
const chipTone = (status) => {
	if (status === "active") return "is-active";
	if (status === "joined" || status === "registered") return "is-joined";
	if (status === "invited") return "is-invited";
	return "is-idle";
};

const TeamPage = () => {
	const { userData, setUserData, language } = useContext(CombinedContext);

	const location = useLocation();
	const navigate = useNavigate();

	const [shopData, setShopData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedStaffProfile, setSelectedStaffProfile] = useState(null);
	/* Tracked separately from the fetched profile so the roster highlights
	   the moment you click, not once the request comes back. */
	const [selectedKey, setSelectedKey] = useState(null);
	const [activeModal, setActiveModal] = useState(null);
	const [activeServiceCategory, setActiveServiceCategory] = useState(null);
	const [banner, setBanner] = useState(null);
	const [bannerExiting, setBannerExiting] = useState(false);

	const serviceCategoryRefs = useRef({});
	const tabsRef = useRef(null);
	const bannerTimers = useRef([]);

	const t = translations[language].teamPage;

	/* Banner timers used to outlive the page and setState on an unmounted tree. */
	useEffect(() => () => bannerTimers.current.forEach(clearTimeout), []);

	/* The onboarding bar deep-links here to open a modal, the same way it does for
	   the profile page. Without this the "Add your team" step navigated to /team
	   and opened nothing — and did nothing at all when the owner was already on
	   this page, which is exactly how a dead step looks.

	   The state is cleared once consumed, otherwise closing the modal and hitting
	   back would pop it open again. */
	useEffect(() => {
		if (!location.state?.openModal) return;

		setActiveModal(location.state.openModal);
		navigate(location.pathname, { replace: true, state: null });
	}, [location.state, location.pathname, navigate]);

	const showBanner = (type, text, duration = 4000) => {
		bannerTimers.current.forEach(clearTimeout);
		bannerTimers.current = [];

		setBanner({ type, text });
		setBannerExiting(false);

		bannerTimers.current.push(setTimeout(() => setBannerExiting(true), duration - 250));
		bannerTimers.current.push(
			setTimeout(() => {
				setBanner(null);
				setBannerExiting(false);
			}, duration),
		);
	};

	useEffect(() => {
		async function load() {
			try {
				const res = await companyBasedRequest.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken);

				setShopData(res.shop);
			} catch (err) {
				console.error("Error loading team:", err);
			} finally {
				setLoading(false);
			}
		}
		if (userData?.user?.shopId) load();
	}, [userData]);

	useEffect(() => {
		if (!shopData || !userData?.user?.userId) return;

		// Find the staff object for the logged-in user
		const selfStaff = shopData.staffs.find((st) => st.userId === userData.user.userId);

		if (selfStaff) {
			loadStaffPrivateInfo(selfStaff);
			return;
		}

		/* An owner who holds no calendar isn't in shop.staffs, so the lookup above
		   misses them and the panel opened on "select a team member" — with their
		   own row sitting right there in the roster. Select them the same as anyone
		   else; loadStaffPrivateInfo only needs the id and a non-invited status. */
		if (shopData.ownerTakesBookings === false && userData.user.role === "owner") {
			loadStaffPrivateInfo({ userId: userData.user.userId, status: "active" });
		}
	}, [shopData, userData]);

	const loadStaffPrivateInfo = async (staff) => {
		if (selectedKey === staff.userId) {
			return;
		}

		if (!staff.staffId && staff.status === "invited") {
			setSelectedKey(null);
			setSelectedStaffProfile(null);
			return;
		}

		setSelectedKey(staff.userId);

		/* Selecting yourself paints from the user already in context before the
		   request goes out. Two reasons: the panel appears instantly instead of
		   after a round trip, and an owner who holds no calendar still gets their
		   profile even if /staff/:id/private refuses them — older builds of that
		   endpoint required a scheduleId, and a refusal there used to leave the
		   panel blank with the row highlighted, which reads as a dead click. */
		const isSelf = staff.userId && staff.userId === userData?.user?.userId;

		if (isSelf) {
			const u = userData.user;

			setSelectedStaffProfile((prev) =>
				prev && prev.staffId === u.userId
					? prev
					: {
							staffId: u.userId,
							firstName: u.firstName,
							lastName: u.lastName,
							fullName: `${u.firstName || ""} ${u.lastName || ""}`.trim(),
							email: u.email,
							phone: u.phone || u.phoneNumber || null,
							photo: u.photo || null,
							bio: u.bio || "",
							role: u.role,
							services: [],
						},
			);
		}

		try {
			const res = await companyBasedRequest.getStaffInfoPrivate(staff.userId, userData.cognitoTokens.AccessToken);
			setSelectedStaffProfile(res.staff);
		} catch (err) {
			// Keep whatever was seeded above rather than blanking the panel.
			console.error("Failed to load staff info:", err);
		}
	};

	const staffServiceIds = Array.isArray(selectedStaffProfile?.services)
		? selectedStaffProfile.services.map((s) => (typeof s === "string" ? s : s.serviceId)).filter(Boolean)
		: [];

	const staffFullServices = (shopData?.services || []).filter((svc) => staffServiceIds.includes(svc.serviceId));

	const servicesByCategory = (shopData?.serviceCategories || []).map((cat) => ({
		category: cat,
		services: staffFullServices.filter((s) => s.categoryId === cat.categoryId),
	}));

	const categoriesWithServices = servicesByCategory.filter((x) => x.services.length > 0);
	const showCategoryChrome = categoriesWithServices.length > 1;

	useEffect(() => {
		if (!categoriesWithServices.length) return;

		setActiveServiceCategory(categoriesWithServices[0].category.categoryId);
	}, [selectedStaffProfile]);

	/* Keep the sticky category rail in sync with whatever section is under it,
	   the same way the shop and staff pages do. */
	useEffect(() => {
		if (!showCategoryChrome) return undefined;

		const firstCategoryId = categoriesWithServices[0].category.categoryId;

		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries
					.filter((e) => e.isIntersecting)
					.map((e) => ({ id: e.target.getAttribute("data-category-id"), top: e.boundingClientRect.top }))
					.filter((x) => x.id);

				if (visible.length) {
					const targetLine = STICKY_OFFSET + 8;
					visible.sort((a, b) => Math.abs(a.top - targetLine) - Math.abs(b.top - targetLine));
					setActiveServiceCategory(visible[0].id);
					return;
				}

				if (window.scrollY < 200) setActiveServiceCategory(firstCategoryId);
			},
			{ root: null, rootMargin: `-${STICKY_OFFSET + 8}px 0px -70% 0px`, threshold: [0.01, 0.1, 0.2] },
		);

		Object.values(serviceCategoryRefs.current).forEach((el) => el && observer.observe(el));
		return () => observer.disconnect();
	}, [selectedStaffProfile, shopData, showCategoryChrome]);

	/* Follow the active tab so it never sits off the edge of the rail */
	useEffect(() => {
		if (!activeServiceCategory || !tabsRef.current) return;
		const activeBtn = tabsRef.current.querySelector(`[data-category-tab="${activeServiceCategory}"]`);
		if (activeBtn) tabsRef.current.scrollTo({ left: Math.max(0, activeBtn.offsetLeft - 16), behavior: "smooth" });
	}, [activeServiceCategory]);

	/* The owner's calendar switch rewrites the roster and the shop's own flag, so
	   both the page and the app-wide shop in context have to be re-read — the
	   calendar tab and the onboarding bar both branch on ownerTakesBookings. */
	const reloadShop = async (override) => {
		try {
			const updated = await companyBasedRequest.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken);

			/* `override` carries fields the caller already knows are newer than
			   anything this read can return — the shop is fetched with a plain
			   (eventually consistent) GetItem, so a refetch issued moments after a
			   write can still hand back the pre-write version. */
			const shop = override ? { ...updated.shop, ...override } : updated.shop;

			setShopData(shop);
			setUserData((prev) => ({ ...prev, shop: { ...prev.shop, ...shop } }));
		} catch (err) {
			console.error("Error reloading shop:", err);
		}
	};

	const handleLaunchStaff = async (staffId) => {
		try {
			const res = await companyBasedRequest.launchStaff(staffId, userData.cognitoTokens.AccessToken);

			showBanner("success", res.message);

			const updated = await companyBasedRequest.getShopWithstaffsByIdPrivate(userData.user.shopId, userData.cognitoTokens.AccessToken);
			setShopData(updated.shop);
		} catch {
			showBanner("error", t.messages.activateFailed);
		}
	};

	if (loading || !shopData) return <TeamPageSkeleton />;

	/* The roster is built from shop.staffs, which holds calendar-holders — so an
	   owner who doesn't take appointments isn't in it. They still belong on this
	   page: it's the shop's people, not the shop's calendars, and selecting
	   yourself here is the only way to edit your own name, photo or bio. Without
	   this row that owner disappears from their own team and loses that entirely.

	   Synthesised on the client rather than injected server-side, because the
	   private shop endpoint also feeds the profile cards and the app-wide shop in
	   context, and none of those should start seeing a non-bookable staff member. */
	const rosterOwner =
		shopData.ownerTakesBookings === false && userData?.user?.role === "owner" && !(shopData.staffs || []).some((s) => s.userId === userData.user.userId)
			? {
					userId: userData.user.userId,
					firstName: userData.user.firstName,
					lastName: userData.user.lastName,
					email: userData.user.email,
					photo: userData.user.photo || "",
					role: "owner",
					status: "active",
					notBookable: true,
					services: [],
				}
			: null;

	/* The owner heads every list of people in the shop. Registration already
	   writes them into shop.staffs first, but the array is rebuilt whenever the
	   roster changes — handing a calendar over and taking it back used to leave
	   them underneath their own staff — so the order is enforced here rather than
	   trusted. Stable sort, so everyone else keeps the order the shop set. */
	const staffs = ownerFirst(shopData, rosterOwner ? [rosterOwner, ...(shopData.staffs || [])] : shopData.staffs || []);
	const counts = {
		active: staffs.filter((s) => s.status === "active").length,
		pending: staffs.filter((s) => s.onboarding?.isComplete && s.status !== "active").length,
		invited: staffs.filter((s) => s.status === "invited").length,
	};

	const serviceTag = (service) => {
		const loc = service.locationType || (service.isHomeService ? "home" : "in_shop");
		if (loc === "home") {
			return (
				<span className="sf-shop-service-tag is-home">
					<House size={12} strokeWidth={2} />
					{t.main.serviceType.home}
				</span>
			);
		}
		if (loc === "online") {
			return (
				<span className="sf-shop-service-tag is-online">
					<Monitor size={12} strokeWidth={2} />
					{t.main.serviceType.online}
				</span>
			);
		}
		return null;
	};

	const roleLabel = selectedStaffProfile?.role ? t.role[selectedStaffProfile.role] : null;

	const viewingNonBookableOwner = shopData.ownerTakesBookings === false && selectedStaffProfile?.role === "owner";

	return (
		<div className="sf-tm-page">
			<div className="sf-tm-content">
				{/* ── HEADER ── */}
				<header className="sf-tm-head">
					<div className="sf-tm-head-row">
						<div className="sf-tm-head-copy">
							{/* {shopData.name && <span className="sf-shop-eyebrow">{shopData.name}</span>} */}
							<h1 className="sf-tm-title">{t.header.title}</h1>
							<p className="sf-tm-subtitle">{t.header.subtitle}</p>
						</div>

						<button type="button" className="sf-tm-manage" onClick={() => setActiveModal("team")}>
							<UserPlus size={17} strokeWidth={2} />
							{t.header.manageStaff}
						</button>
					</div>

					{/* {staffs.length > 0 && (
						<div className="sf-tm-stats">
							<div className="sf-tm-stat">
								<span className="sf-tm-stat-num">{counts.active}</span>
								<span className="sf-tm-stat-label">
									<i className="sf-tm-stat-dot is-active" />
									{t.stats.active}
								</span>
							</div>
							<div className={`sf-tm-stat ${counts.pending > 0 ? "is-flagged" : ""}`}>
								<span className="sf-tm-stat-num">{counts.pending}</span>
								<span className="sf-tm-stat-label">
									<i className="sf-tm-stat-dot is-pending" />
									{t.stats.pending}
								</span>
							</div>
							<div className="sf-tm-stat">
								<span className="sf-tm-stat-num">{counts.invited}</span>
								<span className="sf-tm-stat-label">
									<i className="sf-tm-stat-dot is-invited" />
									{t.stats.invited}
								</span>
							</div>
						</div>
					)} */}
				</header>

				<OwnerCalendarCard shopData={shopData} onChanged={reloadShop} showBanner={showBanner} />

				<div className={`sf-tm-body ${staffs.length === 0 ? "is-empty" : ""}`}>
					{/* ── ROSTER ── */}
					<aside className="sf-tm-rail">
						<section className="sf-tm-section">
							<div className="sf-shop-section-head">
								<h2 className="sf-shop-h2">{t.main.roster}</h2>
								{staffs.length > 0 && <span className="sf-shop-count">{staffs.length}</span>}
							</div>

							{staffs.length > 0 ? (
								<div className="sf-tm-roster">
									{staffs.map((staff, i) => {
										const isJoined = staff.status === "joined" || staff.status === "active";
										const isPending = staff.onboarding?.isComplete && staff.status !== "active";
										const isSelected = Boolean(selectedKey) && selectedKey === staff.userId;
										const isSelf = staff.userId && staff.userId === userData?.user?.userId;
										const badge = staff.role === "owner" || staff.role === "admin" ? t.role[staff.role] : null;

										return (
											<div
												key={staff.staffId || staff.email}
												style={{ "--i": i }}
												className={`sf-tm-card ${isSelected ? "is-selected" : ""} ${isJoined ? "" : "is-locked"}`}
											>
												{/* Stretched hit target, so Launch below stays a real button
												    rather than a control nested inside another one. */}
												<button
													type="button"
													className="sf-tm-card-open"
													aria-pressed={isSelected}
													disabled={!isJoined}
													onClick={() => loadStaffPrivateInfo(staff)}
												>
													<span className="sf-tm-sr">
														{staff.firstName} {staff.lastName}
													</span>
												</button>

												{staff.photo ? (
													<img className="sf-tm-card-avatar" src={staff.photo} alt="" loading="lazy" />
												) : (
													<span className="sf-tm-card-avatar is-fallback">{staff.firstName?.charAt(0).toUpperCase() || "U"}</span>
												)}

												<span className="sf-tm-card-body">
													<span className="sf-tm-card-name">
														{staff.firstName} {staff.lastName}
													</span>
													<span className="sf-tm-card-email">{staff.email}</span>
													<span className={`sf-tm-chip ${staff.notBookable ? "is-idle" : chipTone(staff.status)}`}>
														{staff.notBookable ? t.status.notBookable : t.status[staff.status] || staff.status}
													</span>
												</span>

												{badge ? (
													<span className="sf-tm-tag is-role">{badge}</span>
												) : (
													isSelf && <span className="sf-tm-tag is-you">{t.main.you}</span>
												)}

												{isPending && (
													<button type="button" className="sf-tm-launch" onClick={() => handleLaunchStaff(staff.staffId)}>
														<Rocket size={13} strokeWidth={2} />
														{t.main.launch}
													</button>
												)}
											</div>
										);
									})}
								</div>
							) : (
								<div className="sf-tm-empty">
									<span className="sf-tm-empty-icon">
										<Users size={22} strokeWidth={1.7} />
									</span>
									<p className="sf-tm-empty-title">{t.empty.noStaff}</p>
									<p className="sf-tm-empty-hint">{t.empty.noStaffHint}</p>
								</div>
							)}
						</section>
					</aside>

					{/* ── SELECTED PROFILE ── */}
					<div className="sf-tm-detail">
						{selectedStaffProfile ? (
							<>
								<section className="sf-tm-section">
									<div className="sf-shop-section-head">
										<h2 className="sf-shop-h2">{t.main.profile}</h2>
										<button
											type="button"
											className="sf-tm-edit"
											aria-label={`${t.actions.edit}: ${t.main.profile}`}
											onClick={() => setActiveModal("info")}
										>
											<Edit size={15} strokeWidth={1.9} />
										</button>
									</div>

									<div className="sf-tm-profile" key={selectedKey}>
										<div className="sf-tm-profile-head">
											<div className="sf-tm-avatar-wrap">
												{selectedStaffProfile.photo ? (
													<img className="sf-tm-avatar" src={selectedStaffProfile.photo} alt="" />
												) : (
													<span className="sf-tm-avatar is-fallback">{selectedStaffProfile.firstName?.charAt(0).toUpperCase()}</span>
												)}

												<button
													type="button"
													className="sf-tm-avatar-edit"
													aria-label={`${t.actions.edit}: ${t.main.profile}`}
													onClick={() => setActiveModal("avatar")}
												>
													<Camera size={16} strokeWidth={1.8} />
												</button>
											</div>

											<div className="sf-tm-profile-copy">
												<h2 className="sf-tm-name">{selectedStaffProfile.fullName}</h2>

												{roleLabel && (
													<div className="sf-tm-badges">
														<span className="sf-tm-badge">{roleLabel}</span>
													</div>
												)}
											</div>
										</div>

										{selectedStaffProfile.bio && (
											<div className="sf-tm-bio">
												<p>{selectedStaffProfile.bio}</p>
											</div>
										)}

										<div className="sf-tm-contact">
											<div className="sf-tm-contact-row">
												<span className="sf-tm-contact-icon">
													<Mail size={16} strokeWidth={1.7} />
												</span>
												<span className="sf-tm-contact-text">
													<em>{t.labels.emailShort}</em>
													<strong>{selectedStaffProfile.email}</strong>
												</span>
											</div>

											<div className="sf-tm-contact-row">
												<span className="sf-tm-contact-icon">
													<Phone size={16} strokeWidth={1.7} />
												</span>
												<span className="sf-tm-contact-text">
													<em>{t.labels.phoneShort}</em>
													<strong className={selectedStaffProfile.phone ? "" : "is-empty"}>
														{selectedStaffProfile.phone ? formatPhone(selectedStaffProfile.phone) : t.labels.noPhone}
													</strong>
												</span>
											</div>
										</div>
									</div>
								</section>

								{/* ── SERVICES ── */}
								<section className="sf-tm-section">
									<div className="sf-shop-section-head">
										<h2 className="sf-shop-h2">{t.main.services}</h2>
										{staffFullServices.length > 0 && <span className="sf-shop-count">{staffFullServices.length}</span>}

										{/* Nothing to assign to someone customers can't book. */}
										{!viewingNonBookableOwner && (
											<button
												type="button"
												className="sf-tm-edit"
												aria-label={`${t.actions.edit}: ${t.main.services}`}
												onClick={() => setActiveModal("services")}
											>
												<Edit size={15} strokeWidth={1.9} />
											</button>
										)}
									</div>

									{categoriesWithServices.length > 0 ? (
										<div className="sf-tm-services">
											{showCategoryChrome && (
												<div ref={tabsRef} className="sf-shop-cat-tabs">
													{categoriesWithServices.map(({ category, services }) => (
														<button
															key={category.categoryId}
															type="button"
															data-category-tab={category.categoryId}
															className={`sf-shop-cat-tab ${activeServiceCategory === category.categoryId ? "is-active" : ""}`}
															onClick={() => {
																setActiveServiceCategory(category.categoryId);
																serviceCategoryRefs.current[category.categoryId]?.scrollIntoView({
																	behavior: "smooth",
																	block: "start",
																});
															}}
														>
															{category.name?.[language]}
															<span>{services.length}</span>
														</button>
													))}
												</div>
											)}

											{categoriesWithServices.map(({ category, services }) => (
												<div
													key={category.categoryId}
													ref={(el) => (serviceCategoryRefs.current[category.categoryId] = el)}
													data-category-id={category.categoryId}
													className="sf-shop-cat-card"
												>
													{showCategoryChrome && (
														<div className="sf-shop-cat-header">
															<h3>{category.name?.[language]}</h3>
															<span className="sf-shop-cat-meta">{services.length}</span>
														</div>
													)}

													<div className="sf-shop-cat-body is-open">
														<div>
															{services.map((srv) => (
																<div key={srv.serviceId} className="sf-shop-service">
																	<div className="sf-shop-service-copy">
																		<h4 className="sf-shop-service-name">
																			{srv.name}
																			{serviceTag(srv)}
																		</h4>
																		{srv.description && <p className="sf-shop-service-desc">{srv.description}</p>}
																		<span className="sf-shop-service-time">
																			<Clock size={13} strokeWidth={1.8} />
																			{srv.duration} min
																		</span>
																	</div>

																	<div className="sf-shop-service-action">
																		<span className="sf-shop-service-price">{formatPrice(srv.price, srv.currency)}</span>
																	</div>
																</div>
															))}
														</div>
													</div>
												</div>
											))}
										</div>
									) : viewingNonBookableOwner ? (
										/* "No services assigned" would read as something left undone.
										   Nothing is undone — this owner doesn't take appointments, so
										   services and a calendar don't apply to them. */
										<div className="sf-tm-empty">
											<span className="sf-tm-empty-icon">
												<Clock size={22} strokeWidth={1.7} />
											</span>
											<p className="sf-tm-empty-title">{t.empty.notBookableTitle}</p>
											<p className="sf-tm-empty-hint">{t.empty.notBookableHint}</p>
										</div>
									) : (
										<div className="sf-tm-empty">
											<span className="sf-tm-empty-icon">
												<Clock size={22} strokeWidth={1.7} />
											</span>
											<p className="sf-tm-empty-title">{t.main.noServices}</p>
											<p className="sf-tm-empty-hint">{t.empty.noServicesHint}</p>
										</div>
									)}
								</section>
							</>
						) : (
							staffs.length > 0 && (
								<section className="sf-tm-section">
									<div className="sf-tm-empty">
										<span className="sf-tm-empty-icon">
											<Users size={22} strokeWidth={1.7} />
										</span>
										<p className="sf-tm-empty-title">{t.empty.selectTitle}</p>
										<p className="sf-tm-empty-hint">{t.empty.selectHint}</p>
									</div>
								</section>
							)
						)}
					</div>
				</div>
			</div>

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={bannerExiting} />}

			{activeModal === "team" && (
				<TeamModal
					open={true}
					onClose={() => setActiveModal(null)}
					shopData={shopData}
					onStaffAdded={(newStaff) => {
						setShopData((prev) => ({
							...prev,
							staffs: [...prev.staffs, ...newStaff],
						}));
					}}
					onStaffRemoved={(email) => {
						setShopData((prev) => ({
							...prev,
							staffs: prev.staffs.filter((s) => s.email !== email),
						}));
					}}
					onRoleUpdated={(userId, newRole) => {
						setShopData((prev) => ({
							...prev,
							staffs: prev.staffs.map((s) => (s.userId === userId ? { ...s, role: newRole } : s)),
						}));
					}}
					onInviteSent={(setup) => {
						showBanner("success", t.toast.inviteSent, 10000);

						/* Sending an invite completes the `team` onboarding step server-side.
						   The progress bar reads that off the shop in context, so without
						   this the step only ticked on a full page reload. The invite hands
						   back the setup it just wrote, which is applied over the refetch so
						   the bar can't be undone by a stale read. */
						reloadShop(setup ? { setup } : undefined);
					}}
				/>
			)}

			{activeModal === "services" && (
				<TeamServicesModal
					isOpen
					onClose={() => setActiveModal(null)}
					shopServices={shopData.services}
					staffProfile={selectedStaffProfile}
					onSave={(newServiceIds) => {
						const fullServices = shopData.services.filter((svc) => newServiceIds.includes(svc.serviceId));
						setSelectedStaffProfile((prev) => ({
							...prev,
							services: fullServices,
						}));
					}}
				/>
			)}

			{activeModal === "avatar" && (
				<ProfileUploadModal
					isOpen
					staffProfile={selectedStaffProfile}
					onClose={() => setActiveModal(null)}
					onUploaded={(newUrl) => {
						const bustedUrl = `${newUrl}?t=${Date.now()}`;

						setSelectedStaffProfile((prev) => ({
							...prev,
							photo: bustedUrl,
						}));

						setShopData((prev) => {
							const updatedStaffs = prev.staffs.map((s) => (s.userId === selectedKey ? { ...s, photo: bustedUrl } : s));

							return { ...prev, staffs: updatedStaffs };
						});
					}}
				/>
			)}

			{activeModal === "info" && (
				<StaffInfoModal
					open
					staffProfile={selectedStaffProfile}
					onClose={() => setActiveModal(null)}
					onSave={(updated) => {
						// Update detail panel instantly
						setSelectedStaffProfile((prev) => ({
							...prev,
							...updated,
						}));

						/* Update list. Matched on the key the roster was opened with,
						   because the private profile doesn't always carry a userId —
						   when it didn't, the row matched nothing and kept the old name. */
						setShopData((prev) => {
							const updatedStaffs = prev.staffs.map((s) => (s.userId === selectedKey ? { ...s, ...updated } : s));
							return { ...prev, staffs: updatedStaffs };
						});

						/* Editing yourself has to reach the rest of the app too: the
						   account page, calendars and booking payloads read the name and
						   phone off the context user, which is what forced the refresh. */
						if (selectedKey && selectedKey === userData?.user?.userId) {
							setUserData((prev) => ({
								...prev,
								user: {
									...prev.user,
									firstName: updated.firstName,
									lastName: updated.lastName,
									bio: updated.bio,
									phone: updated.phone,
									phoneNumber: updated.phoneNumber,
								},
							}));
						}
					}}
				/>
			)}
		</div>
	);
};

export default TeamPage;
